import type { ChangeEvent } from "../../api/domain.ts";
import type { Dictionary } from "../../i18n/dictionary.ts";

/**
 * Reading the change log for a human.
 *
 * A change event carries its diff as JSON text in the shape `cms_store::Diff`
 * serializes: a `change` tag plus the fields that tag brings. Nothing here
 * interprets values; it only names what kind of change happened, so the audit
 * trail stays readable without leaking storage details onto the screen.
 */

/** The kinds of change the log distinguishes, plus the unreadable case. */
export type ChangeKind =
  | "created"
  | "updated"
  | "deleted"
  | "translation"
  | "draftAnswer"
  | "unknown";

type EntityDiff = {
  change: "entity";
  old?: unknown;
  new?: unknown;
};

type TaggedDiff = { change?: string } & Partial<EntityDiff>;

const parseDiff = (diff: string): TaggedDiff | undefined => {
  try {
    const parsed: unknown = JSON.parse(diff);
    return typeof parsed === "object" && parsed !== null ? (parsed as TaggedDiff) : undefined;
  } catch {
    return undefined;
  }
};

const entityKind = (diff: TaggedDiff): ChangeKind => {
  const had = diff.old !== undefined && diff.old !== null;
  const has = diff.new !== undefined && diff.new !== null;
  if (!has) return "deleted";
  return had ? "updated" : "created";
};

/** What kind of change one event records. */
export const changeKind = (event: ChangeEvent): ChangeKind => {
  const diff = parseDiff(event.diff);
  if (diff === undefined) return "unknown";
  if (diff.change === "entity") return entityKind(diff);
  if (diff.change === "translation") return "translation";
  if (diff.change === "draft") return "draftAnswer";
  return "unknown";
};

export const changeKindLabel = (dictionary: Dictionary, kind: ChangeKind): string =>
  dictionary.history[kind];

/** The event as one line of prose: what happened, in the reader's language. */
export const describeChange = (dictionary: Dictionary, event: ChangeEvent): string =>
  changeKindLabel(dictionary, changeKind(event));

/** Where the audit trail of one entity lives. */
export const historyHref = (entityType: string, id: string): string =>
  `/history/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}`;

/**
 * The report builder, opened at the moment one change happened. The builder
 * reads `as_of` from the query string and evaluates against the replayed state,
 * which is the as-of support the API already carries.
 */
export const asOfHref = (at: number, view?: string): string =>
  view === undefined || view === ""
    ? `/reports?as_of=${at}`
    : `/reports?as_of=${at}&view=${encodeURIComponent(view)}`;

/** Newest first, which is the order a change log is read in. */
export const newestFirst = <Event extends { at: number }>(events: Event[]): Event[] =>
  [...events].sort((left, right) => right.at - left.at);
